/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import type { NavMethod } from '../../../api/services/articleService'

const VISUAL_SESSION_KEY = 'wire-ui:visual-engagement-session-uuid'

export type VisualEngagementContextValue = {
  enabled: boolean
  articleId: string
  sessionUuid: string
  viewUuid: string
  navMethod?: NavMethod
  pageId?: string
}

const VisualEngagementContext = createContext<VisualEngagementContextValue | null>(null)

function getOrCreateVisualSessionUuid(): string {
  try {
    const existing = window.sessionStorage.getItem(VISUAL_SESSION_KEY)
    if (existing) return existing
    const created = crypto.randomUUID()
    window.sessionStorage.setItem(VISUAL_SESSION_KEY, created)
    return created
  } catch {
    return crypto.randomUUID()
  }
}

export function VisualEngagementProvider({
  articleId,
  enabled = true,
  navMethod,
  pageId,
  children,
}: {
  articleId: string
  enabled?: boolean
  navMethod?: NavMethod
  pageId?: string
  children: ReactNode
}) {
  const [sessionUuid] = useState(() => getOrCreateVisualSessionUuid())
  const [viewUuid] = useState(() => crypto.randomUUID())

  const value = useMemo<VisualEngagementContextValue | null>(() => {
    if (!enabled) return null
    return { enabled, articleId, sessionUuid, viewUuid, navMethod, pageId }
  }, [articleId, enabled, navMethod, pageId, sessionUuid, viewUuid])

  return (
    <VisualEngagementContext.Provider value={value}>
      {children}
    </VisualEngagementContext.Provider>
  )
}

export function useVisualEngagementContext(): VisualEngagementContextValue | null {
  return useContext(VisualEngagementContext)
}